import { useSyncExternalStore } from "react";
import { EN } from "../locales/en";
import { ZH } from "../locales/zh";
import { TA } from "../locales/ta";

/**
 * UI language (buttons, labels, messages). Learning content and generated papers stay in their own language.
 * Keys are the Bahasa Melayu text, so a missing translation simply shows BM.
 */
export type Lang = "ms" | "en" | "zh" | "ta";
export const LANGS: { code: Lang; label: string; short: string; locale: string }[] = [
  { code: "ms", label: "Bahasa Melayu", short: "BM", locale: "ms-MY" },
  { code: "en", label: "English", short: "EN", locale: "en-MY" },
  { code: "zh", label: "中文", short: "中文", locale: "zh-MY" },
  { code: "ta", label: "தமிழ்", short: "தமிழ்", locale: "ta-MY" },
];
const DICT: Record<Exclude<Lang, "ms">, Record<string, string>> = { en: EN, zh: ZH, ta: TA };

function initial(): Lang {
  try {
    const saved = localStorage.getItem("lang");
    if (saved && LANGS.some((l) => l.code === saved)) return saved as Lang;
  } catch { /* storage blocked */ }
  return "ms";
}

let current: Lang = initial();
const listeners = new Set<() => void>();
if (typeof document !== "undefined") document.documentElement.lang = current;

export function getLang(): Lang { return current; }
export function setLang(l: Lang) {
  current = l;
  try { localStorage.setItem("lang", l); } catch { /* storage blocked */ }
  document.documentElement.lang = l;
  listeners.forEach((f) => f());
}
export function useLang(): Lang {
  return useSyncExternalStore((f) => { listeners.add(f); return () => listeners.delete(f); }, () => current, () => current);
}
/** Locale for dates and numbers in the chosen language. */
export const locale = () => LANGS.find((l) => l.code === current)!.locale;

/** Translate BM text; {name} placeholders are filled from vars. */
export function t(ms: string, vars?: Record<string, string | number>): string {
  const s = current === "ms" ? ms : DICT[current][ms] ?? ms;
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s;
}

/** Marks a BM string as translatable where it is stored for later t(): t(TITLES[x]). Returns it unchanged. */
export const tk = (ms: string) => ms;

/** Subject names arrive from the server in BM; these keys let t(name) translate them. */
export const SUBJECT_NAMES = [
  tk("Sains"), tk("Matematik"), tk("Sejarah"), tk("Bahasa Melayu"), tk("Bahasa Inggeris"), tk("Pendidikan Islam"), tk("Pendidikan Moral"),
  tk("Reka Bentuk dan Teknologi"), tk("Pendidikan Jasmani"), tk("Pendidikan Kesihatan"), tk("Pendidikan Seni Visual"), tk("Pendidikan Muzik"),
  tk("Bahasa Arab"), tk("Bahasa Cina"), tk("Bahasa Tamil"), tk("Geografi"),
];
